/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description setup query for message API
 */

import type { Listable, MessageData, Property } from '@zcode/state-type'
import getMessageOriginString from './getMessageOriginString'

interface Args {
  config: {
    entity: Listable | Property
    isMobile: boolean
    isMulti: boolean | undefined
  }
  messageData: MessageData
  oneTap: boolean | undefined
  variationMap: Record<string, string>
}

export interface Message {
  building_ids?: readonly number[]
  email: string | null | undefined
  name: string | undefined
  profile: undefined
  phone: string | null
  listing_ids?: readonly number[]
  message: string | undefined
  message_modified: boolean | undefined
  move_in: string | null | undefined
  origin: string
  viewing_id?: number
}

// helper function to set up the message API query
const setupMessageQuery = (args: Args) => {
  const { config, messageData, variationMap, oneTap } = args

  const { entity, isMobile, isMulti } = config

  const {
    buildingIds,
    email,
    listingIds,
    messageModified,
    moveIn,
    msg,
    name,
    origin,
    phone,
    profile,
    viewingId,
  } = messageData

  // configure core message properties from messageData.
  const message: Message = {
    email,
    name,
    profile,
    phone: phone || null,
    message: msg,
    message_modified: messageModified,
    move_in: moveIn,
    origin: getMessageOriginString({
      origin,
      isMobile,
      multiListingId: isMulti && entity && (entity.listingId ?? `-${entity.buildingId}`),
      variationMap,
      oneTap,
    }),
  }

  if (listingIds?.length) {
    message.listing_ids = listingIds
  } else if (buildingIds?.length) {
    message.building_ids = buildingIds
  } else {
    // no need to call api if we do not have listing_ids or building_ids
    throw new Error('missing property id')
  }

  // append supplemental properities from messageData
  if (viewingId) {
    message.viewing_id = ~~viewingId
  }

  return { message }
}

export default setupMessageQuery
